// src/pages/maintenance/QuickCompleteWorkOrderModal.js
// A fast, additive alternative to CompleteWorkOrderModal.js for simple preventive-maintenance
// tasks (e.g. "clean the debris tray") that don't need root cause/parts/corrective-action — just
// who did it, how long it took, optional notes, and an optional photo. Calls the exact same
// POST /work-orders/:id/complete endpoint (backend/services/maintenanceWoService.js's
// closeWorkOrder() already tolerates a minimal payload), just with a smaller form. The full
// workflow stays unchanged and fully available via the regular "Complete" button.
import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import maintenanceService from '../../services/maintenanceService';
import userService from '../../services/userService';
import SearchableSelect from '../../components/SearchableSelect';

function currentUserId() {
  try {
    const token = localStorage.getItem('token');
    return token ? jwtDecode(token).id : null;
  } catch {
    return null;
  }
}

export default function QuickCompleteWorkOrderModal({ workOrder, onSave, onClose }) {
  const [users, setUsers] = useState([]);
  const [completedByUserIds, setCompletedByUserIds] = useState(() => {
    const me = currentUserId();
    return me ? [String(me)] : [''];
  });
  const [minutesSpent, setMinutesSpent] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { userService.listUsers().then(setUsers).catch(() => setUsers([])); }, []);

  const userOptions = users.map(u => ({ value: u.id, label: u.username || `${u.firstName || ''} ${u.lastName || ''}`.trim() }));

  const setUserAt = (idx, val) => {
    setCompletedByUserIds(list => list.map((v, i) => (i === idx ? val : v)));
  };
  const addUserRow = () => setCompletedByUserIds(list => [...list, '']);
  const removeUserRow = (idx) => setCompletedByUserIds(list => list.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    const pickedIds = completedByUserIds.filter(Boolean).map(Number);
    if (pickedIds.length === 0) { setError('Select at least one person who did the work.'); return; }
    setSaving(true); setError('');
    try {
      const result = await maintenanceService.completeWorkOrder(workOrder.id, {
        actualHours: minutesSpent !== '' ? Number((parseFloat(minutesSpent) / 60).toFixed(2)) : null,
        correctionNotes: notes.trim() || null,
        completedByUserIds: pickedIds,
      });
      if (photoFile) {
        const { uploadUrl, key } = await maintenanceService.getWoPhotoUploadUrl(workOrder.id, photoFile.type);
        await maintenanceService.uploadToPresignedUrl(uploadUrl, photoFile);
        await maintenanceService.addWoPhoto(workOrder.id, { s3Key: key, photoType: 'after', caption: photoFile.name });
      }
      onSave(result);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to complete work order.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">⚡ Quick Complete — {workOrder.woNumber}</h5>
            <button className="btn-close" onClick={onClose} disabled={saving} />
          </div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
              <div className="form-text mb-3" style={{ fontSize: 12 }}>
                For simple tasks that don't need root cause, parts, or a corrective-action writeup.
              </div>

              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Completed By <span className="text-danger">*</span></label>
              {completedByUserIds.map((val, idx) => (
                <div key={idx} className="d-flex gap-2 align-items-start mb-2">
                  <div className="flex-grow-1">
                    <SearchableSelect
                      options={userOptions}
                      value={val}
                      onChange={v => setUserAt(idx, v || '')}
                      placeholder="Select person…"
                      size="sm"
                    />
                  </div>
                  {completedByUserIds.length > 1 && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeUserRow(idx)}>&times;</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn-sm btn-link p-0 mb-3" onClick={addUserRow}>+ Add another person</button>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Time Spent (minutes)</label>
                  <input type="number" step="1" className="form-control form-control-sm" value={minutesSpent} onChange={e => setMinutesSpent(e.target.value)} placeholder="e.g. 10" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Photo (optional)</label>
                  <input type="file" accept="image/*" capture="environment" className="form-control form-control-sm" onChange={e => setPhotoFile(e.target.files?.[0] || null)} />
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Notes (optional)</label>
                  <textarea className="form-control form-control-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn-success btn-sm" disabled={saving}>{saving ? 'Completing…' : '⚡ Mark Completed'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
